import React, { useCallback, useRef, useState } from 'react';
import { Keyboard } from 'react-native';
import Animated from 'react-native-reanimated';
import { CommonActions } from '@react-navigation/native';
import getSymbolFromCurrency from 'currency-symbol-map';
import { addDays, format, isBefore, parseISO, startOfToday } from 'date-fns';
import { AppView, SCROLL_INDICATOR_INSETS, SettingsItemMoneyInput, SettingsItemNavigate, SettingsSection, withAnimatedNavigationHeader, } from 'design-system';
import { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import { IconBoldCalendar, IconGoal } from 'design-system/icons';
import { KeyboardAvoidingButton } from 'design-system/KeyboardAvoidingButton';
import StatusBar from 'design-system/StatusBar';
import Text from 'design-system/Text';
import { muteErrorAndResult, useDateTimePicker, useFetchOld } from 'hooks';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { focus, useAutoFocusInput } from 'hooks/useAutoFocusInput';
import useColors from 'hooks/useColors';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { updatePot } from 'features/pots/actions';
import Alert from 'utils/packages/Alert';
import { isWeb } from '../../../constants';
const strings = {
    dateLabel: 'Target date (optional)',
    placeholder: 'Choose...',
    continueBtn: 'Continue',
    goalAmountLabel: 'Goal amount',
    // alerts
    ok: 'OK',
    alertTitle: 'Hold on 💫',
    invalidDate: 'The date must be in the future.',
    invalidGoalAmount: 'You need to enter a valid amount.',
    smallGoalAmount: 'Your target amount should be more than your current balance.',
};
const EditPotGoalScreen = withAnimatedNavigationHeader(({ navigation, route, scrollHandler }) => {
    const { pot } = route.params;
    const { goal } = pot;
    const goalAmountRef = useRef(null);
    useAutoFocusInput(goalAmountRef);
    const [date, setDate] = useState(goal?.date || '');
    const [goalAmount, setGoalAmount] = useState(goal?.amount ? String(goal?.amount) : '');
    const [, , isFetching, fetch] = useFetchOld(muteErrorAndResult);
    const colors = useColors();
    // A goal date has to be in the future, so the earliest one offered is tomorrow
    const earliestDate = addDays(startOfToday(), 1);
    const { showDateTimePicker } = useDateTimePicker({
        minimumDate: earliestDate,
        initialValue: date ? new Date(date) : earliestDate,
        onSubmitDate: (date) => setDate(format(date, 'yyyy-MM-dd')),
    });
    const clearGoalAmount = useCallback(() => {
        setGoalAmount('');
    }, []);
    const onChangeGoalAmount = useCallback((_text, rawData) => {
        if (rawData !== undefined) {
            setGoalAmount(String(rawData));
        }
    }, []);
    const resetStack = useCallback(() => {
        const potScreen = navigation.getState().routes.find((route) => route.name === 'Pot');
        if (potScreen) {
            navigation.dispatch({
                ...CommonActions.setParams({
                    screenParams: {
                        ...pot,
                        goal: {
                            date,
                            amount: Number(goalAmount),
                        },
                    },
                }),
                source: potScreen.key,
            });
        }
        const potSettingsScreen = navigation.getState().routes.find((route) => route.name === 'PotSettings');
        if (potSettingsScreen) {
            navigation.dispatch({
                ...CommonActions.setParams({
                    pot: {
                        ...pot,
                        goal: {
                            date,
                            amount: Number(goalAmount),
                        },
                    },
                }),
                source: potSettingsScreen.key,
            });
        }
        navigation.goBack();
    }, [date, goalAmount, pot]);
    const handleContinue = useCallback(async () => {
        Keyboard.dismiss();
        if (goal?.amount === Number(goalAmount) && goal.date === date) {
            navigation.goBack();
            return;
        }
        if (!goalAmount || goalAmount === '0') {
            Alert.alert(strings.alertTitle, strings.invalidGoalAmount, [
                {
                    onPress: () => focus(goalAmountRef),
                    text: strings.ok,
                },
            ]);
            return;
        }
        if (Number(goalAmount) < pot.balance.value) {
            Alert.alert(strings.alertTitle, strings.smallGoalAmount, [
                {
                    onPress: () => focus(goalAmountRef),
                    text: strings.ok,
                },
            ]);
            return;
        }
        if (date && isBefore(parseISO(date), new Date())) {
            Alert.alert(strings.alertTitle, strings.invalidDate, [
                {
                    onPress: showDateTimePicker,
                    text: strings.ok,
                },
            ]);
            return;
        }
        const response = await fetch(updatePot({ ...pot, goal: { amount: Number(goalAmount), date } }));
        if (response?.pot) {
            resetStack();
        }
    }, [date, goal?.amount, goal?.date, goalAmount, pot, resetStack, showDateTimePicker]);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const contentContainerStyle = useMarginBottom('paddingBottom', HEIGHT_PLUS_PADDING, paddingHorizontalStyle);
    return (<AppView withFrame={false}>
        <StatusBar barStyle="dark-content" animated/>
        <Animated.ScrollView scrollEventThrottle={16} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS} onScroll={scrollHandler} contentContainerStyle={contentContainerStyle} keyboardShouldPersistTaps="handled">
          <SettingsSection>
            <SettingsItemMoneyInput value={goalAmount} ref={goalAmountRef} allowFontScaling={false} onChangeText={onChangeGoalAmount} onClearTextInput={clearGoalAmount} underlineColorAndroid="transparent" description={strings.goalAmountLabel} selectionColor={colors.text.selection} placeholder={strings.placeholder} icon={<IconGoal gradientKey="skyBlue"/>} prefix={pot?.balance?.currency ? getSymbolFromCurrency(pot.balance.currency) : '£'} maximumFractionalDigits={0} maximumIntegerDigits={10}/>
            <SettingsItemNavigate onPress={showDateTimePicker} description={strings.dateLabel} icon={<IconBoldCalendar gradientKey="lightPink"/>} title={date ? (format(date, 'dd/MM/yyyy')) : (<Text Text-16 Gray-50>
                    {strings.placeholder}
                  </Text>)}/>
          </SettingsSection>
        </Animated.ScrollView>
        <KeyboardAvoidingButton brand disabled={!goalAmount || goalAmount === '0'} onPress={handleContinue} title={strings.continueBtn} loading={isFetching}/>
      </AppView>);
}, ({ route, colors }) => ({
    title: route.params.pot.goal ? 'Edit your goal' : 'Set a goal',
    headerTintColor: colors.elements.primary,
    headerColorOutputRange: [colors.background.dark, colors.cards.onDark],
}));
export default EditPotGoalScreen;
